import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { listJoinedClasses } from '@/api/classes';
import { listJobs } from '@/api/jobs';
import { getMistakes, getMySubmissions } from '@/api/student';
import { Button } from '@/components/ui/button/button';
import { useAuthStore } from '@/stores/authStore';
import type { JobOut } from '@/types';

import styles from './student-home.module.css';

/* ===== 小型线性图标 ===== */
function Icon({ name }: { name: string }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'doc':
      return (
        <svg {...common}>
          <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8z" />
          <path d="M14 3v5h5" />
        </svg>
      );
    case 'fix':
      return (
        <svg {...common}>
          <path d="M14.7 6.3a4.5 4.5 0 0 0-6 6L3 18l3 3 5.7-5.7a4.5 4.5 0 0 0 6-6L14 13l-3-3z" />
        </svg>
      );
    case 'done':
      return (
        <svg {...common}>
          <path d="M22 11.1V12a10 10 0 1 1-5.9-9.1" />
          <path d="M22 4 12 14l-3-3" />
        </svg>
      );
    case 'mistake':
      return (
        <svg {...common}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13z" />
          <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
          <path d="M12 7v5M12 15.5h.01" />
        </svg>
      );
    case 'school':
      return (
        <svg {...common}>
          <path d="M22 10 12 5 2 10l10 5 10-5z" />
          <path d="M6 12.5V17a6 6 0 0 0 12 0v-4.5" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M8 3v4M16 3v4M3 10h18" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z" />
          <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" />
        </svg>
      );
    case 'report':
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 15v-4M12 15V7M17 15v-7" />
        </svg>
      );
    case 'team':
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'book':
      return (
        <svg {...common}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13z" />
          <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
          <path d="M9 8h6" />
        </svg>
      );
    case 'arrow':
      return (
        <svg {...common}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      );
    case 'inbox':
      return (
        <svg {...common}>
          <path d="M22 12h-6l-2 3h-4l-2-3H2" />
          <path d="M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.7 4H7.3a2 2 0 0 0-1.8 1.1z" />
        </svg>
      );
    default:
      return null;
  }
}

/* ===== 日期工具 ===== */
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function dueInfo(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = startOfToday();
  const diff = d.getTime() - today.getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 0) return { tone: 'overdue' as const, label: '已逾期' };
  if (days === 0) return { tone: 'today' as const, label: '今天截止' };
  return { tone: 'todo' as const, label: '待完成' };
}

function formatDate(iso?: string | null) {
  if (!iso) return '未设置截止';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '未设置截止';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}月${d.getDate()}日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 布置时间：精确到分钟 */
function formatAssigned(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}月${d.getDate()}日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 待完成作业：已发布，且 未提交 或 被打回待订正 */
function isPendingJob(job: JobOut) {
  if (job.status !== 'published') return false;
  if (!job.my_submission_status) return true;
  return job.review_status === 'rejected';
}

function scoreTone(score: number): 'green' | 'blue' | 'orange' {
  if (score >= 85) return 'green';
  if (score >= 70) return 'blue';
  return 'orange';
}

const quickEntries = [
  { to: '/jobs', icon: 'doc', title: '我的作业', desc: '待完成 / 已提交 / 老师反馈', tone: 'blue' as const },
  { to: '/my-classes', icon: 'team', title: '我的班级', desc: '管理已加入的班级', tone: 'blue' as const },
  { to: '/mistakes', icon: 'book', title: '错题本', desc: '整理错题并复习', tone: 'purple' as const },
  { to: '/report', icon: 'report', title: '学情报告', desc: '学习进度和薄弱知识点', tone: 'purple' as const },
];

export function StudentHome() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => listJobs(),
  });

  const { data: submissions } = useQuery({
    queryKey: ['my-submissions'],
    queryFn: () => getMySubmissions(),
  });

  const { data: mistakes } = useQuery({
    queryKey: ['mistakes'],
    queryFn: () => getMistakes(),
  });

  const { data: joinedClasses } = useQuery({
    queryKey: ['joined-classes'],
    queryFn: () => listJoinedClasses(),
  });

  const classMap = new Map((joinedClasses ?? []).map((c) => [c.id, c]));

  const jobs = jobsData?.items ?? [];
  const pendingJobs = jobs.filter(isPendingJob);
  pendingJobs.sort((a, b) => {
    const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
    const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
    return da - db;
  });

  const rejectedCount = jobs.filter((j) => j.review_status === 'rejected').length;
  const submittedList = submissions ?? [];

  // 本周已完成（已复核且有分）
  const weekStart = startOfToday();
  weekStart.setDate(weekStart.getDate() - (weekStart.getDay() || 7) + 1);
  const weekDone = submittedList.filter((s) => {
    if (!s.reviewed) return false;
    const d = new Date(s.date);
    return !Number.isNaN(d.getTime()) && d >= weekStart;
  }).length;

  const mistakesTotal = mistakes?.total ?? 0;

  // 老师最新反馈：已复核且有分
  const feedbackList = submittedList
    .filter((s) => s.reviewed && s.score != null)
    .slice(0, 3);

  const overview = [
    {
      icon: 'doc',
      value: pendingJobs.length,
      label: '待完成作业',
      hint: '需尽快提交',
      tone: 'blue',
      to: '/jobs',
    },
    {
      icon: 'fix',
      value: rejectedCount,
      label: '待订正作业',
      hint: '老师已打回',
      tone: 'orange',
      to: '/jobs',
    },
    {
      icon: 'done',
      value: weekDone,
      label: '本周已完成',
      hint: '已提交并批阅',
      tone: 'green',
      to: '/report',
    },
    {
      icon: 'mistake',
      value: mistakesTotal,
      label: '错题提醒',
      hint: '来自本周作业',
      tone: 'purple',
      to: '/mistakes',
    },
  ];

  return (
    <div className={styles.page}>
      {/* 页面头部 */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.greeting}>欢迎回来，{user?.display_name ?? '同学'}</h1>
          <p className={styles.subtitle}>继续完成今天的学习任务，查看老师反馈与错题整理</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/report')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M3 3v18h18" />
            <path d="M7 15v-4M12 15V7M17 15v-7" />
          </svg>
          查看学习报告
        </Button>
      </header>

      {/* 学习概览卡片 */}
      <div className={styles.overviewRow}>
        {overview.map((s) => (
          <Link
            key={s.label}
            to={s.to}
            className={`card card-hover ${styles.overviewCard}`}
          >
            <div className={`${styles.overviewIcon} ${styles[s.tone]}`}>
              <Icon name={s.icon} />
            </div>
            <div className={styles.overviewBody}>
              <div className={styles.overviewValue}>{s.value}</div>
              <div className={styles.overviewLabel}>{s.label}</div>
              <div className={styles.overviewHint}>{s.hint}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* 主体：待完成作业 + 老师反馈 */}
      <div className={styles.mainRow}>
        {/* 待完成作业 */}
        <section className={`card ${styles.taskPanel}`}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>待完成作业</h2>
            <button type="button" className={styles.moreLink} onClick={() => navigate('/jobs')}>
              全部作业
              <Icon name="arrow" />
            </button>
          </div>

          {isLoading && (
            <div className={styles.emptyState}>
              <span className={styles.loadingDot} />
              正在加载任务…
            </div>
          )}

          {!isLoading && pendingJobs.length === 0 && (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>
                <Icon name="inbox" />
              </span>
              <p className={styles.emptyTitle}>暂无待完成作业</p>
              <p className={styles.emptyHint}>你可以查看已完成作业或进入错题本继续复习</p>
            </div>
          )}

          {!isLoading && pendingJobs.length > 0 && (
            <div className={styles.taskList}>
              {pendingJobs.map((job) => {
                const due = dueInfo(job.due_date);
                const isRevision = job.review_status === 'rejected';
                const tone = isRevision ? 'orange' : due ? due.tone : 'todo';
                const label = isRevision ? '待订正' : due ? due.label : '待完成';
                const cls = classMap.get(job.class_id ?? -1);
                return (
                  <div
                    key={job.id}
                    className={`${styles.taskItem} ${styles[`tone-${tone}`]}`}
                  >
                    <div className={styles.taskAccent} />
                    <div className={styles.taskMain}>
                      <div className={styles.taskTitleRow}>
                        <span className={styles.taskTitle}>{job.title}</span>
                        <span
                          className={`tag tag-${
                            tone === 'overdue'
                              ? 'red'
                              : tone === 'today'
                                ? 'orange'
                                : tone === 'orange'
                                  ? 'orange'
                                  : 'blue'
                          }`}
                        >
                          {label}
                        </span>
                      </div>
                      <div className={styles.taskMeta}>
                        <span className={styles.taskMetaItem}>
                          <Icon name="school" />
                          {cls?.name ? `${cls.name} · ${job.subject || '综合学科'}` : job.subject || '综合学科'}
                        </span>
                        <span className={styles.taskMetaItem}>
                          <Icon name="calendar" />
                          布置 {formatAssigned(job.created_at)}
                        </span>
                        <span className={styles.taskMetaItem}>
                          <Icon name="calendar" />
                          截止 {formatDate(job.due_date)}
                        </span>
                      </div>
                    </div>
                    <Button size="small" onClick={() => navigate(`/jobs/${job.id}/submit`)}>
                      {isRevision ? '继续作答' : '开始作答'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* 老师最新反馈 */}
        <section className={`card ${styles.feedbackPanel}`}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>老师最新反馈</h2>
          </div>

          {feedbackList.length === 0 && (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>
                <Icon name="chat" />
              </span>
              <p className={styles.emptyTitle}>暂无批阅反馈</p>
              <p className={styles.emptyHint}>提交作业后，老师的批阅结果会出现在这里</p>
            </div>
          )}

          <div className={styles.feedbackList}>
            {feedbackList.map((f) => {
              const tone = scoreTone(f.score ?? 0);
              return (
                <div key={f.submission_id} className={styles.feedbackItem}>
                  <div className={`${styles.scoreBadge} ${styles[`badge-${tone}`]}`}>
                    <span className={styles.scoreNum}>{f.score}</span>
                    <span className={styles.scoreTotal}>/{f.max_score}</span>
                  </div>
                  <div className={styles.feedbackBody}>
                    <div className={styles.feedbackHead}>
                      <span className={styles.feedbackTitle}>{f.job_title}</span>
                      <span className="tag tag-green">已批阅</span>
                    </div>
                    <p className={styles.feedbackSummary}>
                      老师已批阅，得分 {f.score}/{f.max_score} 分，点击查看详细评语与修改建议。
                    </p>
                    <div className={styles.feedbackFooter}>
                      <span className={styles.feedbackDate}>{f.date}</span>
                      <Button size="small" variant="secondary" onClick={() => navigate(`/jobs/${f.job_id}`)}>
                        查看反馈
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* 快捷入口 */}
      <section className={styles.quickSection}>
        <h2 className={styles.quickTitle}>快捷入口</h2>
        <div className={styles.quickGrid}>
          {quickEntries.map((q) => (
            <button
              key={q.to}
              type="button"
              className={`card card-hover ${styles.quickItem}`}
              onClick={() => navigate(q.to)}
            >
              <span className={`${styles.quickIcon} ${styles[`quick-${q.tone}`]}`}>{<Icon name={q.icon} />}</span>
              <span className={styles.quickTextWrap}>
                <span className={styles.quickName}>{q.title}</span>
                <span className={styles.quickDesc}>{q.desc}</span>
              </span>
              <span className={styles.quickArrow}>
                <Icon name="arrow" />
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
